import { HttpStatus } from '@montugroup/apis-styleguide';
import type { SubmitStocktakeRequestBody } from '@montugroup/circuit-api-contracts';
import { apiPharmacyContract } from '@montugroup/circuit-api-contracts';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import getAuthBearerToken from '@montugroup/circuit-shared/utils/getAuthBearerToken';
import { useQueryClient } from '@tanstack/react-query';
import { isErrorResponse } from '@ts-rest/core';

import { INVENTORY_PRODUCTS_QUERY_KEY } from '@/hooks/useGetInventoryProducts';

export type StocktakeLines = SubmitStocktakeRequestBody['lines'];

type SubmitStocktakeCallbacks = Readonly<{
  onSuccess: () => void;
  onStockChanged: () => void;
  onError: () => void;
}>;

const client = getTsPharmacyClient();

const isStockChangedError = (error: unknown): boolean =>
  isErrorResponse(error, apiPharmacyContract.submitStocktake) && error.status === HttpStatus.CONFLICT;

/**
 * Submits a stocktake and refreshes the inventory products when it succeeds or when stock changed since it started.
 * @returns `submitStocktake`, which calls `onStockChanged` when the server rejects the stocktake as stale (409) and `onError` on any other failure, and `isPending`.
 */
export function useSubmitStocktake() {
  const queryClient = useQueryClient();
  const refreshProducts = () => queryClient.invalidateQueries({ queryKey: [INVENTORY_PRODUCTS_QUERY_KEY] });

  const { mutate, isPending } = client.submitStocktake.useMutation({
    onSuccess: () => refreshProducts(),
    onError: (error) => {
      if (isStockChangedError(error)) {
        return refreshProducts();
      }
      return undefined;
    },
  });

  const submitStocktake = (lines: StocktakeLines, { onSuccess, onStockChanged, onError }: SubmitStocktakeCallbacks) =>
    mutate(
      { headers: { authorization: getAuthBearerToken() }, body: { data: { lines } } },
      { onSuccess, onError: (error) => (isStockChangedError(error) ? onStockChanged() : onError()) },
    );

  return { submitStocktake, isPending };
}
